import React, { FC, useMemo, useEffect, memo } from 'react';
import { useQuery } from '@apollo/client';
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';
import _filter from 'lodash/filter';
import _map from 'lodash/map';
import ErrorMessage from '@minecraft.error-message';
import { helpers } from '@minecraft.utils';
import { appConfig as globalAppConfig } from '@minecraft.app-config';
import { gqlDocument, gqlCountryEnum as COUNTRY } from '@minecraft.graphql-types';
import { SelectOption } from '@minecraft.typings';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import {
  getUpdatedParentDocs,
  generateSelectOptionsBasedOnField,
  FiltersNameEnum,
  FormItem,
  useFiltersLabel,
  FilterDrawerType,
} from '@blocs.filter';
import { FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { Documents } from '../../FilterDrawerCollection';
import { QUERY_DOCUMENTS } from './DocumentsCategoryGraphQL';

const { getAllowedLocations } = helpers;

interface Props {
  type: FilterDrawerType;
}

const DocumentsCategory: FC<Props> = ({ type }) => {
  const filtersLabelOfAudience = useFiltersLabel(type);

  const { data, error, loading } = useQuery(QUERY_DOCUMENTS);

  const { isCategoryMatchedSearch, assignOptionsToCategoryFilters } = useDrawerSearchFilterNameOrCategory(
    FiltersCategoryNameEnum.documents
  );
  const { filtersCounts } = useDrawerFiltersCounts();

  const documentsOptions = useMemo(() => {
    const { isUsCaAllowed, isGbIeAllowed, isAuNzAllowed } = getAllowedLocations();

    const parentDocs = _sortBy(
      _filter(data?.documentTypes, (document) => !_isEmpty(document?.subdocs)),
      'name'
    );

    const internationalDocs = getUpdatedParentDocs(parentDocs, [globalAppConfig.allCountriesCode]);
    const uSDocs = isUsCaAllowed ? getUpdatedParentDocs(parentDocs, [COUNTRY.US]) : [];
    const cADocs = isUsCaAllowed ? getUpdatedParentDocs(parentDocs, [COUNTRY.CA]) : [];
    const gBiEDocs = isGbIeAllowed ? getUpdatedParentDocs(parentDocs, [COUNTRY.GB, COUNTRY.IE]) : [];
    const aUnZDocs = isAuNzAllowed ? getUpdatedParentDocs(parentDocs, [COUNTRY.AU, COUNTRY.NZ]) : [];

    const getDocumentOptions = (documents: gqlDocument[]): SelectOption[] => {
      return _map(documents, (document) => ({
        value: document.id,
        label: document.name,
        options: generateSelectOptionsBasedOnField(
          document?.subdocs,
          'name',
          false,
          filtersCounts?.[FiltersCountNameEnum.documentIds]
        ),
      }));
    };

    return [
      {
        value: `${globalAppConfig.allCountriesCode}`,
        label: 'International',
        options: getDocumentOptions(internationalDocs),
      },
      {
        value: `${COUNTRY.US}`,
        label: 'United States',
        options: getDocumentOptions(uSDocs),
      },
      {
        value: `${COUNTRY.CA}`,
        label: 'Canada',
        options: getDocumentOptions(cADocs),
      },
      {
        value: `${[COUNTRY.GB, COUNTRY.IE]}`,
        label: 'United Kingdom & Ireland',
        options: getDocumentOptions(gBiEDocs),
      },
      {
        value: `${[COUNTRY.AU, COUNTRY.NZ]}`,
        label: 'Australia & New Zealand',
        options: getDocumentOptions(aUnZDocs),
      },
    ];
  }, [data, filtersCounts]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (documentsOptions.length > 0) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.documents]: documentsOptions,
      });
    }
  }, [documentsOptions]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <FormItem $fullWidth $marginValue={2}>
        <Documents name={FiltersNameEnum.documents} label={filtersLabelOfAudience[FiltersNameEnum.documents]} />
      </FormItem>
    )
  );
};

export default memo(DocumentsCategory);
